import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Typography,
  IconButton,
  Box,
  InputAdornment,
  useTheme,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { saveAs } from "file-saver";
import useMediaQuery from "@mui/material/useMediaQuery";

import SupportImage from '../../../components/images/career.webp';
import { AZURE_DEVOPS, EXECUTIVE_PG_DEVOPS_PROGRAM } from "../../../constants/AzureDevops";
import { FULL_STACK } from "../../../FullStack/FSConstants";
import { trackLead } from "../../../utils/fbConversion";

const inputStyle = {
  background: "#ffffff",
  borderRadius: "12px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    "& fieldset": { borderColor: "#e4e4e7" },
    "&:hover fieldset": { borderColor: "#b9b9c3" },
    "&.Mui-focused fieldset": { borderColor: "#5e17eb" },
  },
};

export default function BroucherPopupv2(props) {
  const { open, setOpen, refer, brochure, heading } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loader, setLoader] = useState(false);
  const [formError, setFormError] = useState("");
  const [gclid, setGclid] = useState("");

  const handleClose = () => setOpen(false);

  const url =
    heading === FULL_STACK
      ? "https://paperlive.github.io/paperlive_learning/Full_Stack_Development_with_GenAI.pdf"
      : heading === AZURE_DEVOPS
      ? "https://paperlive.github.io/paperlive_learning/DevOps%20and%20Azure%20with%20GenAI.pdf"
      :
      heading === EXECUTIVE_PG_DEVOPS_PROGRAM ?
      "https://paperlive.github.io/paperlive_learning/Executive-PG-in-DevOps-&-Multi-Cloud-Paperlive-Learning.pdf"
      : "https://paperlive.github.io/paperlive_learning/DevOps-and-Cloud.pdf";


  const [formValues, setFormValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    website: window.location.href.slice(0, 250),
    description: window.location.href.includes("full-stack-web-development")
      ? "Full Stack Web Development"
      : window.location.href.includes("devops-and-cloud-training-online")
      ? "DevOps and Cloud with GenAI Master Program"
      : "DevOps and Cloud with GenAI Job Ready Program",
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  // Validators
  const validateFirstName = (v) => (!v ? "First name is required" : "");
  const validateLastName = (v) => (!v ? "Last name is required" : "");
  const validateEmail = (v) => {
    if (!v) return "Email is required";
    const r = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return !r.test(v) ? "Invalid email address" : "";
  };
  const validatePhone = (v) => {
    if (!v) return "Phone number is required";
    return /^\d{10}$/.test(v) ? "" : "Phone number must be 10 digits";
  };

  const handleChange = (e, field) => {
    const value = e.target.value;
    setFormValues({ ...formValues, [field]: value });

    let err = "";
    if (field === "firstName") err = validateFirstName(value);
    if (field === "lastName") err = validateLastName(value);
    if (field === "email") err = validateEmail(value);
    if (field === "phone") err = validatePhone(value);

    setErrors({ ...errors, [field]: err });
  };

  // GCLID handling
  useEffect(() => {
    const urlGclid = new URLSearchParams(window.location.search).get("gclid");
    if (urlGclid) {
      localStorage.setItem("gclid", urlGclid);
      setGclid(urlGclid);
    } else {
      setGclid(localStorage.getItem("gclid"));
    }
  }, []);

  // Handle download + Zoho submit
  const handleDownload = () => {
    setLoader(true);

    trackLead({
      contentName: "Brochure Download",
      email: formValues.email,
      phone: formValues.phone,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
    });

    // Submit to Zoho first (synchronously)
    const form = document.forms["WebToLeads774394000000345033"];
    if (form) form.submit();

    window.open(url);
    setTimeout(() => {

      fetch(url)
        .then((res) => res.blob())
        .then((blob) => {
          saveAs(
            blob,
            heading === AZURE_DEVOPS
              ? "DevOps and Azure with GenAI.pdf"
              : heading === FULL_STACK
              ? "Full Stack Development with GenAI.pdf"
              : "DevOps and Cloud with GenAI Job Ready Program.pdf"
          );
        })
        .catch(() => {});
    }, 400);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const f1 = validateFirstName(formValues.firstName);
    const f2 = validateLastName(formValues.lastName);
    const f3 = validateEmail(formValues.email);
    const f4 = validatePhone(formValues.phone);

    if (f1 || f2 || f3 || f4) {
      setErrors({ firstName: f1, lastName: f2, email: f3, phone: f4 });
      setFormError(f1 || f2 || f3 || f4);
      return;
    }

    handleDownload();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}
    >
      <IconButton
        aria-label="Close Free Consultation Form"
        onClick={handleClose}
        sx={{ position: "absolute", right: 14, top: 14, zIndex: 10 }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0 }}>
        <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
          {!isMobile && (
            <Box flex={1}>
              <img
                loading="lazy"
                src={SupportImage}
                alt="Brochure"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          )}

          <Box flex={1} sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="h5" fontWeight={700} mb={1}>
              {brochure
                ? "Download Brochure"
                : refer
                ? "Referral Information"
                : "Talk to Our Consultant"}
            </Typography>

            <Typography color="text.secondary" mb={3}>
              Enter your details and get instant access.
            </Typography>

            <form
              id="webform774394000000345033"
              name="WebToLeads774394000000345033"
              method="POST"
              action="https://crm.zoho.in/crm/WebToLeadForm"
              onSubmit={handleFormSubmit}
            >
              <input
                style={{ display: "none" }}
                name="xnQsjsdp"
                defaultValue="687200ebfd3b2fe7528be8fc456683865bab8cc38280d6ff4dda9352f2525ee8"
              />
              <input
                style={{ display: "none" }}
                name="xmIwtLD"
                defaultValue="cc543c4baa5e86451f2b99c193e1fc869fb07de37de357f307632b791e840601f5a20e4d91f34cf35597c60ad50dac4c"
              />
              <input style={{ display: "none" }} name="actionType" defaultValue="TGVhZHM=" />
              <input
                style={{ display: "none" }}
                name="returnURL"
                defaultValue="https://paperlive.in/thankyou"
              />
              <input type="hidden" id="zc_gad" name="zc_gad" value={gclid} />
              <input type="hidden" id="Website" name="Website" value={formValues.website} />
              <input type="hidden" id="Description" name="Description" value={formValues.description} />

              <Box display="grid" gap="14px">
                <TextField
                  value={formValues.firstName}
                  onChange={(e) => handleChange(e, "firstName")}
                  label="First Name"
                  id="First_Name"
                  name="First Name"
                  error={!!errors.firstName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AssignmentIndIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputStyle}
                />

                <TextField
                  value={formValues.lastName}
                  onChange={(e) => handleChange(e, "lastName")}
                  label="Last Name"
                  id="Last_Name"
                  name="Last Name"
                  error={!!errors.lastName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AssignmentIndIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputStyle}
                />

                <TextField
                  value={formValues.email}
                  onChange={(e) => handleChange(e, "email")}
                  label="Email"
                  id="Email"
                  name="Email"
                  error={!!errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputStyle}
                />

                <TextField
                  value={formValues.phone}
                  onChange={(e) => handleChange(e, "phone")}
                  label="Phone Number"
                  id="Phone"
                  name="Phone"
                  error={!!errors.phone}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIphoneIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputStyle}
                />

                <div style={{ color: "red" }}>{formError}</div>

                <Button
                  aria-label="Download Brochure"
                  variant="contained"
                  fullWidth
                  disabled={loader}
                  type="submit"
                  sx={{
                    mt: 1,
                    borderRadius: "12px",
                    fontWeight: 700,
                    background: "#5e17eb",
                    ":hover": { background: "#4d13c6" },
                  }}
                >
                  {loader ? "Downloading..." : "Download"}
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}